import { Component, DestroyRef, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defaultIfEmpty, defer, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../../icon/icon.component';
import { CoursePlayerHeaderComponent } from '../../../course-player-header/course-player-header.component';
import {
  CoursePlayerService,
  CourseNavigationItem,
  CourseStructure,
  PlayerMode
} from '../../../../../services/course-player.service';
import {
  getModuleIcon,
  getModuleIconClass,
  getModuleProgress,
  isModuleComplete
} from '../../learner-modal.utils';
import { formatScoreParts, ScoreDisplayParts } from '../../../../utils/score-display.utils';
import { computeRunningScore, RunningScoreSummary } from '../../../../utils/running-score.utils';
import { ScorePtsPipe } from '../../../../pipes/score-pts.pipe';
import { isQuizPassing } from '@shared/utils/quiz-pass.utils';
import { UnitResultView } from '../../../../passing-policy/passing-policy-results.service';
import { Certificate, CourseService } from '../../../../../courses/course.service';
import { findCertificateForCourse } from '../../pure-functions/certificate-for-course';

@Component({
  selector: 'app-learner-course-summary',
  standalone: true,
  imports: [CommonModule, IconComponent, CoursePlayerHeaderComponent, ScorePtsPipe],
  templateUrl: './course-summary.component.html',
  styleUrls: ['./course-summary.component.scss']
})
export class CourseSummaryComponent {
  @Input() courseData: any;
  @Input() courseStructure: CourseStructure | null = null;
  @Input() courseProgressMap: Map<string, any> = new Map();
  // KAN-410: authoritative per-unit layered-passing-policy results (keyed by
  // unitId), the SAME computeUnitResult output the Unit Overview renders. Lets
  // this summary flag a quiz that violates the unit points-floor / per-quiz gate
  // instead of re-deriving pass/fail from the quiz's own mark alone.
  @Input() unitResultsMap: Map<string, UnitResultView> = new Map();
  // KAN-411 round 3: genuine course completion (all modules done AND the pass
  // gate satisfied), computed by the parent modal. Gates the "Course Complete"
  // summary badge and the certificate-download block so a gate-blocked course
  // (all modules done but below threshold / floor-violated) no longer claims
  // Completed or offers a certificate that was never issued.
  @Input() courseComplete = false;
  @Input() currentItemIndex = 0;
  @Input() totalItems = 1;
  @Input() canGoToPrevious = false;
  @Input() canGoToNext = false;
  @Input() isMobile = false;
  @Input() showTimeline = true;
  @Input() isFullscreen = false;
  /** KAN-620: false where the browser cannot fullscreen the player (iPhone). */
  @Input() showFullscreen = true;
  @Input() playerMode: PlayerMode = 'learner';

  @Output() onNavigatePrevious = new EventEmitter<void>();
  @Output() onNavigateNext = new EventEmitter<void>();
  @Output() onTimelineToggle = new EventEmitter<void>();
  @Output() onFullscreenToggle = new EventEmitter<void>();
  @Output() onCloseModal = new EventEmitter<void>();
  /** KAN-679: the learner's certificate for this course was found; the player
   *  closes and its host opens /certificates/:id. */
  @Output() onOpenCertificate = new EventEmitter<string>();

  /** KAN-679: 'loading' while the certificate lookup is in flight (button
   *  disabled), 'not-ready' when none was found or the lookup failed. */
  readonly certificateLookup = signal<'idle' | 'loading' | 'not-ready'>('idle');

  private destroyRef = inject(DestroyRef);

  constructor(
    private coursePlayerService: CoursePlayerService,
    private courseService: CourseService
  ) {}

  /** KAN-679: instructors previewing or test-taking hold no certificate. */
  get isPreviewMode(): boolean {
    return this.playerMode !== 'learner';
  }

  /**
   * KAN-679: open the learner's certificate for this course. Looked up at click
   * time because the completion that led to this slide may have just issued it.
   * Wrapped in defer() because getUserCertificates throws synchronously when no
   * user is signed in.
   */
  viewCertificate(): void {
    if (this.isPreviewMode || this.certificateLookup() === 'loading') return;
    this.certificateLookup.set('loading');
    defer(() => this.courseService.getUserCertificates())
      .pipe(take(1), defaultIfEmpty([] as Certificate[]), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (certificates) => {
          const match = findCertificateForCourse(certificates, this.courseData?.id);
          this.certificateLookup.set(match ? 'idle' : 'not-ready');
          if (match) this.onOpenCertificate.emit(match.id);
        },
        error: () => this.certificateLookup.set('not-ready')
      });
  }

  getModuleIcon(item: CourseNavigationItem): string {
    return getModuleIcon(item, this.courseProgressMap, this.coursePlayerService);
  }

  getModuleIconClass(item: CourseNavigationItem): string {
    return getModuleIconClass(item, this.courseProgressMap, this.coursePlayerService);
  }

  getModuleProgress(moduleId: string): any {
    return getModuleProgress(moduleId, this.courseStructure, this.courseProgressMap, this.coursePlayerService);
  }

  isModuleComplete(moduleId: string): boolean {
    return isModuleComplete(moduleId, this.courseStructure, this.courseProgressMap, this.coursePlayerService);
  }

  /**
   * KAN-127 Phase 3: Returns fully-formatted score parts for a module's progress.
   * Consumes the shared formatScoreParts util (Phase 0) so precision, the
   * anti-contradiction clamp, and the showPoints heuristic all live in one place.
   *
   * @param moduleId  The module id to look up in courseProgressMap
   * @param passingScore  Optional passing threshold (default 80)
   */
  /** KAN-627: the best quiz row is prior completion credit -- shown as "Credited", not a score. */
  isPriorCreditForModule(moduleId: string): boolean {
    return this.getModuleProgress(moduleId)?.quizPriorCredit === true;
  }

  /** KAN-621 AC-2: name the source course that supplied inherited credit, so a
   *  learner can see WHERE a credited row came from. Returns null when the row
   *  is not inherited or no source title was carried on the API row. */
  getInheritedSourceCourseTitle(moduleId: string): string | null {
    const row: any = this.getModuleProgress(moduleId);
    if (!row || row.inheritedCredit !== true) return null;
    return row.inheritedSourceCourseTitle || null;
  }

  getScorePartsForModule(moduleId: string, passingScore = 80): ScoreDisplayParts {
    const progress = this.getModuleProgress(moduleId);
    return formatScoreParts(progress, passingScore);
  }

  /**
   * KAN-239 (reopened): Decide a module's quiz pass/fail so the Course Progress
   * list NEVER diverges from the result footer / under-video badge. Raw
   * earned/total points are ground truth (they reproduce the exact
   * computeQuizResult boundary), so when present we recompute from them and
   * IGNORE any stored `quizPassed` boolean — correcting already-stored bad data
   * (e.g. a backend that aliased quizPassed to "completed") without a migration.
   * Precedence:
   *   1. Raw points (earned/total) when available -> isQuizPassing.
   *   2. Stored `quizPassed` boolean — only when raw points are absent
   *      (instructor-preview emit carries no points but a correct boolean).
   *   3. Last-resort: the integer-rounded quizScore via isQuizPassing.
   */
  isModuleQuizPassed(moduleId: string, passingScore?: number | null): boolean {
    const progress = this.getModuleProgress(moduleId);
    if (!progress) return false;

    const earned = progress.quizEarnedPoints;
    const total = progress.quizTotalPoints;
    if (earned !== null && earned !== undefined && total > 0) {
      return isQuizPassing((earned / total) * 100, passingScore ?? 80);
    }

    if (typeof progress.quizPassed === 'boolean') {
      return progress.quizPassed;
    }

    return isQuizPassing(progress.quizScore ?? 0, passingScore ?? 80);
  }

  /**
   * KAN-410: the authoritative UnitResultView for the item's parent unit, or
   * null. Standalone (direct) modules have no unit and never carry a unit-level
   * floor/gate, so they resolve to null (the quiz's own pass mark still governs).
   */
  private getUnitResultForItem(item: CourseNavigationItem): UnitResultView | null {
    const unitId = (item as any).parentUnitId || (item as any).unitId;
    return unitId ? this.unitResultsMap.get(unitId) || null : null;
  }

  /**
   * KAN-410: the per-quiz points-floor (VS3) violation for this item, from the
   * authoritative unit result, matched on the item's quizId. Present => the quiz
   * earned below the unit's minimum-points floor even if it cleared its own
   * pass mark.
   */
  getFloorViolationForItem(
    item: CourseNavigationItem
  ): { quizTitle?: string; earnedPoints: number; minRequired: number } | null {
    const quizId = (item as any).quizId;
    if (!quizId) return null;
    const result = this.getUnitResultForItem(item);
    return result?.floorViolations?.find((fv) => fv.quizId === quizId) || null;
  }

  /**
   * KAN-410: the per-quiz gate (VS2 requireEachQuizToPass) violation for this
   * item - the quiz did not clear its own required passing score under a unit
   * that requires every quiz to pass.
   */
  getGateViolationForItem(item: CourseNavigationItem): { quizTitle?: string } | null {
    const quizId = (item as any).quizId;
    if (!quizId) return null;
    const result = this.getUnitResultForItem(item);
    return result?.gateViolations?.find((gv) => gv.quizId === quizId) || null;
  }

  /**
   * KAN-410: the Course Summary per-quiz verdict, reconciled with the unit-level
   * authoritative result. A quiz that violates the unit points-floor or the
   * per-quiz gate is NOT "Passed" here - matching the Unit Overview - even when
   * it clears its own pass mark. Absent any unit-level violation, falls back to
   * the quiz's own pass verdict (isModuleQuizPassed).
   */
  isQuizPassedForItem(item: CourseNavigationItem): boolean {
    if (this.getFloorViolationForItem(item) || this.getGateViolationForItem(item)) {
      return false;
    }
    return this.isModuleQuizPassed(item.id, (item as any).passingScore);
  }

  /**
   * KAN-465: running cumulative points score over the attempted quizzes,
   * against the course passing threshold. Same pure util as the timeline
   * sidebar so the two surfaces can never disagree.
   */
  getRunningScore(): RunningScoreSummary {
    return computeRunningScore(
      this.courseProgressMap.values(),
      this.courseData?.passingPolicy?.coursePassingThreshold ?? null
    );
  }

  // KAN-465 cleanup: the old averageScore field (unweighted mean of quizScore
  // integers) was computed here but rendered nowhere — removed rather than
  // resurrected; the running points percentage above is the correct formula.
  getCourseCompletionStats(): { completedModules: number; totalModules: number } {
    if (!this.courseStructure) {
      return { completedModules: 0, totalModules: 0 };
    }

    const modules = this.courseStructure.navigationItems.filter(
      item => item.type === 'module' || item.type === 'unit_quiz'
    );

    let completedCount = 0;

    modules.forEach(module => {
      const progressKey = this.coursePlayerService.getProgressKey(module);
      const progress = this.courseProgressMap.get(progressKey);
      if (progress?.moduleCompleted) {
        completedCount++;
      }
    });

    return {
      completedModules: completedCount,
      totalModules: modules.length
    };
  }

  getGroupedProgressItems(): Array<{
    type: 'unit' | 'direct' | 'summary';
    unitId?: string;
    unitName?: string;
    items: CourseNavigationItem[];
  }> {
    if (!this.courseStructure) return [];

    const groups: Array<{
      type: 'unit' | 'direct' | 'summary';
      unitId?: string;
      unitName?: string;
      items: CourseNavigationItem[];
    }> = [];

    const processedItems = new Set<string>();
    const processedUnits = new Set<string>();
    let currentDirectModuleGroup: CourseNavigationItem[] = [];

    for (const item of this.courseStructure.navigationItems) {
      if (processedItems.has(item.id)) continue;

      if (item.type === 'course_summary') {
        if (currentDirectModuleGroup.length > 0) {
          groups.push({ type: 'direct', items: [...currentDirectModuleGroup] });
          currentDirectModuleGroup = [];
        }
        groups.push({ type: 'summary', items: [item] });
        processedItems.add(item.id);
        continue;
      }

      if (item.type === 'unit_intro') {
        processedItems.add(item.id);
        continue;
      }

      if (item.isDirectModule || !item.parentUnitId) {
        currentDirectModuleGroup.push(item);
        processedItems.add(item.id);
      } else if (item.parentUnitId && !processedUnits.has(item.parentUnitId)) {
        if (currentDirectModuleGroup.length > 0) {
          groups.push({ type: 'direct', items: [...currentDirectModuleGroup] });
          currentDirectModuleGroup = [];
        }

        const unitId = item.parentUnitId;
        const unitModules = this.courseStructure.navigationItems.filter(
          unitItem => unitItem.parentUnitId === unitId &&
                      unitItem.type !== 'unit_intro' &&
                      unitItem.type !== 'course_summary'
        );

        const unitName = item.parentUnitName ||
                         this.courseStructure.navigationItems.find(
                           i => i.type === 'unit_intro' && i.parentUnitId === unitId
                         )?.name?.replace(' - Overview', '') ||
                         `Unit ${unitId}`;

        groups.push({
          type: 'unit',
          unitId,
          unitName,
          items: unitModules
        });

        unitModules.forEach(m => processedItems.add(m.id));
        processedUnits.add(unitId);
      }
    }

    if (currentDirectModuleGroup.length > 0) {
      groups.push({ type: 'direct', items: currentDirectModuleGroup });
    }

    return groups;
  }
}
